/*
 * Full-page audit log viewer — Data Center → Audit Trail Log → {form}.
 *
 *  Driven by the same global /api/v1/audit/trail endpoint the inline
 *  <AuditTrailButton> uses, so the rendering, columns and back-end
 *  query are identical app-wide.  Difference vs the popup is layout
 *  only: this page lives as the BODY of AppShell with a full-height
 *  grid, date-range chip, search box, status filter and CSV export —
 *  matching the visual treatment of every other Data Center list
 *  page (Sample Backlog, ModuleListView, etc.).
 *
 *  Page id → formName mapping is centralised in AT_FORM_BY_ID so a
 *  new audit-trail nav entry only needs (a) a new nav row in
 *  navData.ts with id="at-foo" and (b) one row in the map below.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocal } from '../../utils/datetime';
import { AuditTimeline } from './AuditTimeline';
import { AI_AUDIT_FORMS, aiFormFromNavId } from './aiAuditForms';

/** Audit-row shape returned by GET /api/v1/audit/trail. */
interface AuditRow {
  oid:              string;
  createdDate?:     string | null;
  createdBy?:       string | null;
  operationType?:   string | null;
  formName?:        string | null;
  sourceId?:        string | null;
  recordId?:        string | null;   // ALPACA audit "ID" column (AuditData.ID)
  propertyName?:    string | null;
  oldValue?:        string | null;
  newValue?:        string | null;
  comment?:         string | null;
  reasonForChange?: string | null;
}

/** Map nav page id → the set of ALPACA AuditData.FormName values that
 *  feed that audit screen.  Several ALPACA audit views aggregate MORE
 *  than one FormName (e.g. Field Data Entry shows both 'Field Data Entry'
 *  and 'Sample Registration'), so each entry is a LIST — taken verbatim
 *  from ALPACA's AuditlogsummaryViewController filters so the AI grid
 *  shows exactly the same rows as ALPACA GoLive. */
const AT_FORMS_BY_ID: Record<string, string[]> = {
  // ── Settings / master-data forms — one audit-log view each (2026-06-27). ──
  'at-holidays':       ['Holidays'],
  'at-method':         ['Method'],
  'at-city':           ['City'],
  'at-country':        ['Country'],
  'at-state':          ['State'],
  'at-department':     ['Department'],
  'at-position':       ['Position'],
  'at-division':       ['Division'],
  'at-paymentterms':   ['Payment Terms'],
  'at-paymenttermssetup': ['Payment Terms Setup'],
  'at-region':         ['Region'],
  'at-adcchain':       ['Analysis Department Chain'],
  // Dedicated Case Narrative audit view (note edits made from the Case Narrative
  // form, which is open to any user — 2026-06-18 decision — and audited here).
  'at-casenarrative':  ['Case Narrative'],
  'at-batchreport':    ['Batch Reporting'],
  'at-custreport':     ['Custom Reporting'],
  'at-eddgen':         ['EDD Report Generator'],
  'at-fielddata':      ['Field Data Entry', 'Sample Registration'],
  'at-l3subout':       ['Level 3 Subout Data Review'],
  // ALPACA's "MOR" screens write the DWQR / Drinking Water Quality Reports
  // FormNames in the audit table.
  'at-morreport':      ['Drinking Water Quality Reports'],
  'at-morpapp':        ['DWQR Pending Approval'],
  'at-morpdel':        ['DWQR Pending Delivery'],
  'at-openquotes':     ['Open Quotes'],
  // Project Notes audit view — note CRUD (FormName 'Project Notes').
  // Case-narrative activity (review ticks + open edits, FormName 'Case
  // Narrative') now has its OWN view above ('at-casenarrative').
  'at-projectnotes':   ['Project Notes'],
  'at-reptrack':       ['Report Tracking'],
  'at-repval':         ['Report Validation', 'Level 2 Report View'],
  'at-rappr':          ['Result Approval'],
  'at-restrack':       ['Result Entry'],
  'at-sampreg':        ['Sample Registration', 'SampleBottleAllocation', 'ReceivedDate',
                        'SampleConditionCheck', 'Image', 'Notes', 'Attachment', 'Test Assignment',
                        'Sample Container pH Check'],
  'at-samplesites':    ['Sample Transfer', 'Sample Registration', 'SampleBottleAllocation',
                        'Tests', 'TestGuide', 'Sample Parameter', 'PrepMethod',
                        'Report Tracking', 'Notes', 'Field Data Entry'],
  'at-samptransfer':   ['Sample Transfer', 'Sample Registration', 'SampleBottleAllocation'],
  'at-sampaloc':       ['Sampling Allocation'],
  'at-suboutresentry': ['Subout Sample Result Entry'],
  'at-suboutresval':   ['Subout Sample Result Validation', 'Level 2 Subout Data Review'],
  'at-subouttest':     ['Subout Test Order'],
  'at-tests':          ['Tests', 'TestGuide'],
};

// AI_AUDIT_FORMS moved to ./aiAuditForms (shared with AppShell's per-form nav).

/** Audit date-range options — mirror ALPACA's audit grid (1M/3M/6M/1Y/2Y/5Y),
 *  computed as Today minus N months/years so the row window (and the count)
 *  lines up with ALPACA, which uses DateTime.Today.AddMonths/AddYears (NOT a
 *  fixed day count). */
function rangeStartIso(range: string): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);                 // align to Today, like ALPACA's DateTime.Today
  switch (range) {
    case '1M': d.setMonth(d.getMonth() - 1); break;
    case '6M': d.setMonth(d.getMonth() - 6); break;
    case '1Y': d.setFullYear(d.getFullYear() - 1); break;
    case '2Y': d.setFullYear(d.getFullYear() - 2); break;
    case '5Y': d.setFullYear(d.getFullYear() - 5); break;
    case 'All': d.setFullYear(d.getFullYear() - 100); break;   // effectively "all time"
    case '3M':
    default:   d.setMonth(d.getMonth() - 3); break;
  }
  // Local yyyy-MM-dd (avoid the UTC shift toISOString would introduce).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const rangeLabel = (range: string): string =>
  ({ '1M': '1 month', '3M': '3 months', '6M': '6 months', '1Y': '1 year', All: 'all time' }[range] ?? range);

export interface AuditTrailPageProps {
  /** Page id from the nav (e.g. 'at-custreport').  When unrecognised,
   *  the page falls back to showing ALL forms' audit rows since the
   *  user clearly meant to browse the global audit log. */
  pageId:    string;
  /** Human display label used in the page title + breadcrumb.  Comes
   *  from pageTitles[pageId] in AppShell. */
  navTitle?: string;
}

export function AuditTrailPage({ pageId, navTitle }: AuditTrailPageProps) {
  // Two "catalog" views share the Business-Object dropdown + /audit/forms
  // catalogue: Data Center → Audit Trail Log → All Forms (at-all) shows the LEGACY
  // ALPACA forms (non-AI), and the new "Audit Trail Log AI" module (at-ai) shows
  // ONLY the AI-migrated forms (AI_AUDIT_FORMS).  isCatalog drives the shared
  // dropdown/catalog UI for both; isAiLog picks the AI-vs-legacy split.
  const isAiLog      = pageId === 'at-ai';
  const isCatalog    = pageId === 'at-all' || isAiLog;
  // Per-form AI audit log (dynamic nav leaf `at-aiform-<slug>`, built by AppShell
  // from the forms that actually have audit rows) — a SINGLE-form log, decoded
  // back to its exact FormName.  Not a catalog; it flows through the same
  // single-form path as the settings/master-data audit views (AT_FORMS_BY_ID).
  const aiFormName   = aiFormFromNavId(pageId);
  // Resolve the set of FormNames for this audit page (see AT_FORMS_BY_ID).
  // Undefined when the page id isn't mapped — we show a clear message
  // rather than blanking the screen.
  const formNames    = isCatalog ? undefined
                     : aiFormName ? [aiFormName]
                     : AT_FORMS_BY_ID[pageId];
  const formNamesCsv = formNames?.join(',') ?? '';

  const [rows,   setRows]   = useState<AuditRow[]>([]);
  const [load,   setLoad]   = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [err,    setErr]    = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [range,  setRange]  = useState<string>('3M');   // matches ALPACA's default audit date filter
  const [opFilter, setOpFilter] = useState<string>('all');
  const [total,     setTotal]     = useState(0);        // true total matching the filter
  const [truncated, setTruncated] = useState(false);    // true when more rows exist than were loaded
  const [view,      setView]      = useState<'grid' | 'timeline'>('grid'); // Grid vs roadmap Timeline
  // ── Business Object picker (All Forms view only) — lets the user narrow the
  // global audit log to ANY audited form, not just the hardcoded nav entries.
  // 'ALL' = every form (all=true); otherwise query that single FormName.
  const [formCatalog, setFormCatalog] = useState<{ formName: string; rows: number }[]>([]);
  const [selectedForm, setSelectedForm] = useState<string>('ALL');

  // Catalogue of every audited FormName (with counts) for the catalog-view dropdown
  // (both All Forms and the AI log).
  useEffect(() => {
    if (!isCatalog) return;
    let alive = true;
    api.get('/api/v1/audit/forms', { params: { sinceDate: rangeStartIso(range) } })
      .then(res => {
        if (!alive) return;
        const list = ((res.data as { data?: { formName: string; rows: number }[] }).data) ?? [];
        setFormCatalog(list);
      })
      .catch(() => { if (alive) setFormCatalog([]); });
    return () => { alive = false; };
  }, [isCatalog, range]);

  // SCOPED catalogue — the split source (§AI_AUDIT_FORMS).
  //  • AI log: list the FULL AI_AUDIT_FORMS inventory — EVERY form AI has audit
  //    trail WIRED, even ones with 0 rows in the range.  A form showing "(0)" is
  //    audited but simply hasn't been exercised yet; a form that is NOT in this
  //    list has NO audit trail at all.  So the AI log doubles as the coverage
  //    inventory — you see exactly which forms AI audits (user request
  //    2026-07-09).  Live row counts are merged in from the catalogue.
  //  • All Forms (legacy): only forms that actually have audit rows — the XAF
  //    form universe isn't enumerable, so we list what has data.
  const scopedCatalog = useMemo(() => {
    if (isAiLog) {
      const counts = new Map(formCatalog.map(f => [f.formName, f.rows]));
      return [...AI_AUDIT_FORMS]
        .map(name => ({ formName: name, rows: counts.get(name) ?? 0 }))
        .sort((a, b) => (b.rows - a.rows) || a.formName.localeCompare(b.formName));
    }
    return formCatalog.filter(f => !AI_AUDIT_FORMS.has(f.formName));
  }, [formCatalog, isAiLog]);

  const load_ = useCallback(async () => {
    if (!isCatalog && !formNamesCsv) {
      setErr('No audit log mapped for this nav page.');
      return;
    }
    setLoad(true); setErr(null);
    const t0 = performance.now();
    try {
      const since = rangeStartIso(range);   // yyyy-MM-dd, matching ALPACA's Today.AddMonths/AddYears
      // Catalog view (All Forms / AI log): 'ALL' → the SCOPED set of FormNames
      // (legacy for at-all, AI for at-ai) so the two logs are split; a specific
      // pick → that one FormName.  Mapped nav pages always use their fixed CSV.
      let params: Record<string, unknown>;
      if (isCatalog) {
        if (selectedForm === 'ALL') {
          const scopedNames = scopedCatalog.map(f => f.formName);
          if (scopedNames.length === 0) {
            // Catalogue not loaded yet, or genuinely no forms in this split/range.
            setRows([]); setTotal(0); setTruncated(false);
            setLoadMs(performance.now() - t0); setLoad(false);
            return;
          }
          params = { formNames: scopedNames.join(','), sinceDate: since };
        } else {
          params = { formNames: selectedForm, sinceDate: since };
        }
      } else {
        params = { formNames: formNamesCsv, sinceDate: since };
      }
      const res = await api.get('/api/v1/audit/trail', { params });
      const body = res.data as {
        data?: AuditRow[];
        meta?: { totalCount?: number; truncated?: boolean };
      };
      const data = body.data ?? [];
      setRows(data);
      setTotal(body.meta?.totalCount ?? data.length);
      setTruncated(!!body.meta?.truncated);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Failed to load audit trail.';
      setErr(msg);
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoad(false);
    }
  }, [formNamesCsv, range, isCatalog, selectedForm, scopedCatalog]);

  useEffect(() => { void load_(); }, [load_]);

  // ── Filter pipeline ──
  const opCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows) {
      const k = String(r.operationType ?? 'Other');
      m[k] = (m[k] ?? 0) + 1;
    }
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    let v = rows;
    if (opFilter !== 'all') v = v.filter(r => (r.operationType ?? '') === opFilter);
    const t = search.trim().toLowerCase();
    if (t) {
      v = v.filter(r => [r.createdBy, r.formName, r.propertyName, r.oldValue, r.newValue, r.comment, r.recordId]
        .some(x => String(x ?? '').toLowerCase().includes(t)));
    }
    return v;
  }, [rows, search, opFilter]);

  // Local timezone, 24-hour, no AM/PM (CLAUDE.md §14b.6) — fmtLocal force-UTCs
  // the wire string then formats en-GB; falls back to the raw value if unparseable.
  const fmtDate = (v?: string | null) => (v ? (fmtLocal(v) || String(v)) : '');

  const title = navTitle
    ?? (isAiLog ? 'Audit Trail Log (AI Forms)'
        : pageId === 'at-all' ? 'Audit Trail Log — All Forms'
        : formNames?.[0])
    ?? 'Audit Trail';

  // Shared-grid columns.  Only Date (custom format) and Action (status pill)
  // need a renderer; the rest are plain black text via the default renderer.
  const gridCols: AppColumn<AuditRow>[] = [
    { field: 'createdDate',  header: 'Date Changed',    width: 150, render: r => fmtDate(r.createdDate), text: r => fmtDate(r.createdDate) },
    { field: 'createdBy',    header: 'Changed By',      width: 110 },
    { field: 'formName',     header: 'Business Object', width: 150 },
    { field: 'operationType', header: 'Action',         width: 110,
      render: r => (r.operationType ? <span className={'adg-pill ' + opVariant(r.operationType)}>{r.operationType}</span> : null),
      text:   r => r.operationType ?? '' },
    { field: 'recordId',     header: 'ID',              width: 130 },
    { field: 'propertyName', header: 'Property Field',  width: 150 },
    { field: 'oldValue',     header: 'Old Value' },
    { field: 'newValue',     header: 'New Value' },
    { field: 'comment',      header: 'Comment',         width: 200 },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Data Center</span><i className="ti ti-chevron-right" />
            <span>Audit Trail Log</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{title}</span>
          </div>
          {/* Shared FormTitle — the ONE page heading (§18.6): nav icon/colour
              via navMeta(pageId) + the TOP load-time chip. */}
          <div className="dp-title-row">
            <FormTitle pageId={pageId} title={title} loadMs={loadMs} loading={load} />
          </div>
          <div className="dp-subtitle">
            Read-only audit history for <strong>{title}</strong>
            &nbsp;·&nbsp;{load ? '…' : total.toLocaleString()} entries (last {rangeLabel(range)})
            {truncated && !load && <> · showing most recent {rows.length.toLocaleString()}</>}
          </div>
        </div>
      </div>

      {/* Grid area: toolbar + search sit ON the grey page (BO UI Standard v2);
          only the grid card below is white. */}
      <div className="bl-grid-area">
        {/* Toolbar on its OWN full-width row — shared <AppToolbar>: date-range +
            view toggle in the left slot, then the canonical Refresh · Export to.
            Audit pages never write so no New / Edit / Delete is shown. */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* Business Object picker — catalog views (All Forms / AI log) only.
                    Lets the user jump to ANY audited form in this split (not just the
                    hardcoded nav entries).  Options come from the SCOPED catalogue so
                    the legacy log and the AI log never overlap. */}
                {isCatalog && (
                  <div className="sa-date-wrap">
                    <i className="ti ti-forms sa-date-prefix" />
                    <span className="sa-date-label">Form</span>
                    <select className="sa-date-sel" value={selectedForm}
                            onChange={e => setSelectedForm(e.target.value)}
                            style={{ maxWidth: 260 }}>
                      <option value="ALL">
                        {isAiLog ? 'All AI Forms' : 'All Forms'} ({scopedCatalog.length || '…'})
                      </option>
                      {scopedCatalog.map(f => (
                        <option key={f.formName} value={f.formName}>{f.formName} ({f.rows.toLocaleString()})</option>
                      ))}
                    </select>
                  </div>
                )}
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
                  value={TRACKING_RANGES.find(r => r.label === range)?.days ?? null}
                  onChange={(d) => setRange(TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All')} />
                {/* Grid ↔ Timeline (roadmap) view toggle — same data, two renders. */}
                <div className="atl-toggle" role="tablist" aria-label="Audit view">
                  <button type="button" role="tab" aria-selected={view === 'grid'}
                          className={view === 'grid' ? 'active' : ''}
                          onClick={() => setView('grid')}>
                    <i className="ti ti-table" aria-hidden="true" /> Grid
                  </button>
                  <button type="button" role="tab" aria-selected={view === 'timeline'}
                          className={view === 'timeline' ? 'active' : ''}
                          onClick={() => setView('timeline')}>
                    <i className="ti ti-timeline" aria-hidden="true" /> Timeline
                  </button>
                </div>
              </div>
            }
            onRefresh={load_}
            refreshing={load}
            exportData={{
              filename: 'audit-trail-' + (navTitle ?? pageId ?? 'all').replace(/\s+/g, '_'),
              ...gridExportMatrix(filtered, gridCols),
            }}
          />
        </div>

        {/* Search — shared .list-search-row on the grey page, never inside the card. */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" value={search}
                   onChange={e => setSearch(e.target.value)}
                   placeholder="Search across user / property / values / reason…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          {filtered.length !== rows.length && (
            <span className="search-match-count">
              {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        {/* ONE white grid card — notices/filter chips + grid + its single footer. */}
        <div className="bl-card bl-card-flex">
          {/* Truncation notice — shown when the form has more rows than the
              grid loads (high-volume forms like Result Entry).  Mirrors the
              practical limit ALPACA gets from paging. */}
          {truncated && !load && !err && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', background: '#fff7ed', borderBottom: '1px solid #fed7aa',
              color: '#9a3412', fontSize: 12,
            }}>
              <i className="ti ti-info-circle" />
              Showing the most recent {rows.length.toLocaleString()} of {total.toLocaleString()} entries —
              narrow the date range or use search to see older entries.
            </div>
          )}

          {/* Operation-type filter chips — auto-derived from the data. */}
          {Object.keys(opCounts).length > 1 && (
            <div className="sa-filter-row">
              <span className="sa-filter-lbl">Operation:</span>
              <button className={'sa-filter-btn' + (opFilter === 'all' ? ' active' : '')}
                      onClick={() => setOpFilter('all')}>
                All ({rows.length})
              </button>
              {Object.entries(opCounts).map(([k, n]) => (
                <button key={k}
                        className={'sa-filter-btn' + (opFilter === k ? ' active' : '')}
                        onClick={() => setOpFilter(k)}>
                  {k} ({n})
                </button>
              ))}
            </div>
          )}

          <style>{`
            .atl-toggle{display:inline-flex;border:1px solid var(--ai-line);border-radius:6px;overflow:hidden}
            .atl-toggle button{border:none;background:var(--ai-card);color:var(--ai-muted);font-size:12px;padding:5px 12px;cursor:pointer;display:inline-flex;align-items:center;gap:5px}
            .atl-toggle button.active{background:var(--brand-accent, var(--ai-orange));color:#fff}
            .atl-toggle button:not(.active):hover{background:var(--ai-row-hover)}
          `}</style>
          {err && !load ? (
            <div className="bl-empty" style={{ color: '#b91c1c', flexDirection: 'column', gap: 6 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
              <div>{err}</div>
            </div>
          ) : view === 'timeline' ? (
            /* Shared roadmap timeline (same component the per-form audit popup uses). */
            <AuditTimeline rows={filtered} loading={load} />
          ) : (
            /* Shared data grid — styling, per-column filters, sort, footer /
               pagination and load-time are all standardised in <AppDataGrid>. */
            <AppDataGrid<AuditRow>
              rows={filtered}
              columns={gridCols}
              getRowId={(r, i) => `${r.oid}-${i}`}
              loading={load}
              loadMs={loadMs}
              emptyText="No audit entries."
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Status-pill variant by operation type — green for adds, BLUE for value-changes,
 *  red for deletes.  Drives the grid Action pill (the Timeline colours live in the
 *  shared <AuditTimeline>). */
function opVariant(op?: string | null): 'ok' | 'warn' | 'alert' | 'info' {
  const k = (op ?? '').toLowerCase();
  if (k.includes('add') || k.includes('insert') || k.includes('creat')) return 'ok';
  if (k.includes('update') || k.includes('edit') || k.includes('chang')) return 'info';
  if (k.includes('delete') || k.includes('remove')) return 'alert';
  return 'info';
}
